/**
 * CSV UTILITIES
 * 
 * Utilities for parsing and mapping CSV imports.
 * Now integrates with collectionParser for smart field extraction.
 * 
 * TAG HANDLING:
 * When the parser detects a tag pattern (e.g., "CATEGORY-Subcategory"),
 * it splits on hyphen and saves to the tags array instead of collectionName.
 * This prevents tags from appearing as collections on brand pages.
 */

import {
  applyParsedCollection,
  needsParsing
} from './collectionParser';
import { FINISH_OPTIONS } from '../types/index';
type Finish = typeof FINISH_OPTIONS[number];

// Supported item types for import
export type ImportItemType = 'polish' | 'plate' | 'transfer' | 'embellishment' | 'tool' | 'powder' | 'acrylic';

// Item type display info
export const ITEM_TYPE_INFO: Record<ImportItemType, { label: string; emoji: string; description: string }> = {
  polish: { label: 'Nail Polish', emoji: '💅', description: 'Nail lacquers, gel polishes, etc.' },
  plate: { label: 'Stamping Plates', emoji: '🔲', description: 'Metal stamping plates for nail art' },
  transfer: { label: 'Decals & Transfers', emoji: '🎨', description: 'Stickers, waterslides, vinyl, tattoos' },
  embellishment: { label: 'Embellishments', emoji: '✨', description: 'Charms, rhinestones, foils, glitter' },
  tool: { label: 'Tools', emoji: '🛠️', description: 'Brushes, dotting tools, lamps, etc.' },
  powder: { label: 'Powders', emoji: '🌟', description: 'Chrome, aurora, buffing powders' },
  acrylic: { label: 'Acrylics', emoji: '💎', description: 'Acrylic powders, liquids, tips' },
};

// Base fields shared by all item types
const BASE_FIELDS = {
  brand: { label: 'Brand', required: true },
  name: { label: 'Name', required: true },
  quantity: { label: 'Quantity', required: false },
  notes: { label: 'Private Notes', required: false },
  publicNotes: { label: 'Public Notes', required: false },
  tags: { label: 'Tags (free-form)', required: false },
  isFavorite: { label: 'Favorite', required: false },
  price: { label: 'Purchase Price', required: false },
  storage: { label: 'Storage Location', required: false },
  barcode: { label: 'Barcode/SKU', required: false },
  photoUrl: { label: 'Photo URL', required: false },
  // Usage tracking
  swatched: { label: 'Swatched (Yes/No)', required: false },
  timesUsed: { label: 'Times Used', required: false },
  lastUsedDate: { label: 'Last Used Date', required: false },
};

// Fields we support for polish imports
export const POLISH_FIELDS = {
  ...BASE_FIELDS,
  name: { label: 'Polish Name', required: true },
  category: { label: 'Category', required: false },
  colorDescription: { label: 'Color Description', required: false },
  line: { label: 'Line (e.g., Insta-Dri, Ultra Chrome)', required: false },
  swatchNumber: { label: 'Swatch Number', required: false },
  polishType: { label: 'Polish Type (Nail Lacquer/Gel/etc.)', required: false },
  
  // ============================================
  // RELEASE TYPE SYSTEM
  // ============================================
  // Primary release type
  releaseType: { label: 'Release Type (core/collection/event/box/etc.)', required: false },
  releaseYear: { label: 'Release Year', required: false },
  releaseSeason: { label: 'Release Season (Spring/Summer/Fall/Winter)', required: false },
  
  // Collection fields (releaseType: collection)
  collectionName: { label: 'Collection Name', required: false },
  collection: { label: 'Collection Name (alias)', required: false },  // Alias for collectionName
  collectionInfo: { label: 'Collection Info (combined - will parse date)', required: false },
  
  // Event fields (releaseType: event)
  eventSource: { label: 'Event Name (PPU/HHC/etc.)', required: false },
  eventTheme: { label: 'Event Theme', required: false },
  eventInfo: { label: 'Event Info (combined - will parse)', required: false },
  
  // Box fields (releaseType: box)
  boxName: { label: 'Box Name (e.g., Polished Gamers Box)', required: false },
  boxTheme: { label: 'Box Theme', required: false },
  
  // Group Custom fields (releaseType: groupCustom)
  groupName: { label: 'Group Name (e.g., Facebook group)', required: false },
  groupTheme: { label: 'Group Theme', required: false },
  
  // Exclusive fields (releaseType: exclusive)
  exclusiveTo: { label: 'Exclusive To (retailer/store)', required: false },
  exclusiveTheme: { label: 'Exclusive Theme', required: false },
  
  // Charity fields (releaseType: charity)
  charityName: { label: 'Charity Name', required: false },
  charityCause: { label: 'Charity Cause', required: false },
  
  // Convention fields (releaseType: convention)
  conventionName: { label: 'Convention Name', required: false },
  
  // Collab fields (releaseType: collab)
  collabWith: { label: 'Collab With (brand/person)', required: false },
  collabTheme: { label: 'Collab Theme', required: false },
  
  // Set/Duo fields
  setName: { label: 'Set Name (for duos/trios)', required: false },
  setType: { label: 'Set Type (duo/trio/quad/set)', required: false },
  
  // Re-release tracking
  isRerelease: { label: 'Is Re-release (Yes/No)', required: false },
  originalReleaseYear: { label: 'Original Release Year', required: false },
  originalReleaseSeason: { label: 'Original Release Season', required: false },
  releaseNotes: { label: 'Release Notes', required: false },
  
  // Combined/parsing fields (for messy data)
  releaseInfo: { label: 'Release Info (combined - will parse)', required: false },
  dateField: { label: 'Date (standalone - extracts year/season)', required: false },
  
  // ============================================
  // STRUCTURED TAGS
  // ============================================
  baseType: { label: 'Base Type (creme/crelly/jelly/sheer)', required: false },
  finish: { label: 'Finish (shimmer/metallic/chrome/etc.)', required: false },
  holoType: { label: 'Holo Type (linear/scattered/glitter)', required: false },
  colorShift: { label: 'Color Shift (duochrome/multichrome)', required: false },
  particles: { label: 'Particles (glitter/flakes/etc.)', required: false },
  specialFeatures: { label: 'Special Features (magnetic/thermal/etc.)', required: false },
  colorFamily: { label: 'Color Family (red/blue/purple/etc.)', required: false },
  utility: { label: 'Utility (topper/undie/stamps well)', required: false },
  colorCharacter: { label: 'Color Character (bright/neon/pastel/etc.)', required: false },
  
  // ============================================
  // STATUS FLAGS
  // ============================================
  hasSwatched: { label: 'Has Swatched (Yes/No)', required: false },
  isRareLE: { label: 'Rare/LE Flag', required: false },
  onWishlist: { label: 'Wishlist', required: false },
  isoPriority: { label: 'ISO Priority (iso/diso/unicorn)', required: false },
  
  // ============================================
  // DESTASH
  // ============================================
  isForDestash: { label: 'For Destash (Yes/No)', required: false },
  destashPrice: { label: 'Destash Price', required: false },
  destashCondition: { label: 'Destash Condition', required: false },
} as const;

// Fields for stamping plate imports
export const PLATE_FIELDS = {
  ...BASE_FIELDS,
  name: { label: 'Plate Name/Number', required: true },
  plateType: { label: 'Plate Type (Stamping Plate/Stencil)', required: false },
  plateSize: { label: 'Plate Size', required: false },
  plateShape: { label: 'Plate Shape (Round/Rectangle/Square)', required: false },
  imageCount: { label: 'Number of Images', required: false },
  collection: { label: 'Collection/Series', required: false },
} as const;

// Fields for transfer/decal imports
export const TRANSFER_FIELDS = {
  ...BASE_FIELDS,
  name: { label: 'Decal Name', required: true },
  transferType: { label: 'Type (Sticker/Waterslide/Vinyl/Tattoo)', required: false },
  collection: { label: 'Collection/Series', required: false },
} as const;

// Fields for embellishment imports
export const EMBELLISHMENT_FIELDS = {
  ...BASE_FIELDS,
  name: { label: 'Embellishment Name', required: true },
  embellishmentType: { label: 'Type (Charm/Rhinestone/Foil/Glitter/etc.)', required: false },
  color: { label: 'Color', required: false },
  size: { label: 'Size', required: false },
} as const;

// Fields for tool imports
export const TOOL_FIELDS = {
  ...BASE_FIELDS,
  name: { label: 'Tool Name', required: true },
  toolType: { label: 'Type (Brush/Dotting Tool/Lamp/Cleanup/etc.)', required: false },
  material: { label: 'Material', required: false },
} as const;

// Fields for powder imports
export const POWDER_FIELDS = {
  ...BASE_FIELDS,
  name: { label: 'Powder Name', required: true },
  powderType: { label: 'Type (Chrome/Aurora/Holographic/Buffing/etc.)', required: false },
  color: { label: 'Color', required: false },
  effect: { label: 'Effect', required: false },
} as const;

// Fields for acrylic imports
export const ACRYLIC_FIELDS = {
  ...BASE_FIELDS,
  name: { label: 'Acrylic Name', required: true },
  acrylicType: { label: 'Type (Powder/Liquid/Tips/Forms/etc.)', required: false },
  color: { label: 'Color', required: false },
} as const;

// Get fields for a specific item type
export function getFieldsForType(itemType: ImportItemType) {
  switch (itemType) {
    case 'polish': return POLISH_FIELDS;
    case 'plate': return PLATE_FIELDS;
    case 'transfer': return TRANSFER_FIELDS;
    case 'embellishment': return EMBELLISHMENT_FIELDS;
    case 'tool': return TOOL_FIELDS;
    case 'powder': return POWDER_FIELDS;
    case 'acrylic': return ACRYLIC_FIELDS;
    default: return POLISH_FIELDS;
  }
}

export type PolishFieldKey = keyof typeof POLISH_FIELDS;
export type PlateFieldKey = keyof typeof PLATE_FIELDS;
export type TransferFieldKey = keyof typeof TRANSFER_FIELDS;
export type EmbellishmentFieldKey = keyof typeof EMBELLISHMENT_FIELDS;
export type ToolFieldKey = keyof typeof TOOL_FIELDS;
export type PowderFieldKey = keyof typeof POWDER_FIELDS;
export type AcrylicFieldKey = keyof typeof ACRYLIC_FIELDS;
export type AnyFieldKey = PolishFieldKey | PlateFieldKey | TransferFieldKey | EmbellishmentFieldKey | ToolFieldKey | PowderFieldKey | AcrylicFieldKey;

// Common column name variations that map to our fields (for all item types)
const COLUMN_ALIASES: Record<string, string[]> = {
  // Common fields across all types
  brand: ['brand', 'maker', 'company', 'manufacturer', 'indie', 'brand name'],
  name: ['name', 'polish name', 'polish', 'color name', 'colour name', 'shade', 'title', 'item name', 'product', 'plate name', 'plate number', 'plate', 'tool name', 'powder name', 'decal name'],
  quantity: ['quantity', 'qty', 'count', 'amount', 'bottles', '# owned', 'owned'],
  notes: ['notes', 'note', 'comments', 'comment', 'private notes', 'my notes'],
  publicNotes: ['public notes', 'public', 'review', 'public comment'],
  tags: ['tags', 'tag', 'labels', 'keywords', 'custom tags'],
  isFavorite: ['favorite', 'favourite', 'fave', 'fav', 'loved', 'love'],
  price: ['price', 'purchase price', 'cost', 'paid', 'amount paid', 'retail', 'retail price', '$'],
  storage: ['storage', 'storage location', 'location', 'shelf', 'drawer', 'helmer', 'box', 'bin', 'where'],
  barcode: ['barcode', 'sku', 'upc', 'code'],
  photoUrl: ['photo', 'photo url', 'image', 'image url', 'picture', 'pic', 'swatch', 'swatch url'],
  
  // Usage tracking
  swatched: ['swatched', 'has swatch', 'swatch done'],
  hasSwatched: ['hasswatched', 'has swatched'],
  timesUsed: ['times used', 'used count', 'use count', 'times worn'],
  lastUsedDate: ['last used', 'last used date', 'last worn'],
  
  // Polish-specific - basic
  category: ['category', 'type', 'polish type', 'formula', 'kind'],
  polishType: ['polishtype', 'polish type', 'formula type'],
  colorDescription: ['color description', 'colour description', 'description', 'color', 'colour', 'desc'],
  line: ['line', 'product line', 'sub-brand', 'sub brand', 'line name'],
  swatchNumber: ['swatch number', 'swatchnumber', 'swatch #', 'swatch num', 'swatch no'],
  
  // Release system - main fields
  releaseType: ['release type', 'releasetype', 'status', 'availability'],
  releaseYear: ['release year', 'releaseyear', 'year released', 'year'],
  releaseSeason: ['release season', 'releaseseason', 'season released', 'season'],
  
  // Collection fields
  collection: ['collection name', 'coll name', 'collection', 'series'],
  collectionName: ['collectionname', 'collection name'],
  collectionInfo: ['collection info', 'collectioninfo'],
  
  // Event fields
  eventSource: ['event name', 'event source', 'eventsource', 'ppu', 'hella handmade', 'hhc', 'event'],
  eventTheme: ['event theme', 'eventtheme', 'theme'],
  eventInfo: ['event info', 'eventinfo', 'event details', 'polish pickup'],
  
  // Box fields
  boxName: ['box name', 'boxname', 'subscription box', 'sub box'],
  boxTheme: ['box theme', 'boxtheme'],
  
  // Group fields
  groupName: ['group name', 'groupname', 'group', 'facebook group', 'fb group'],
  groupTheme: ['group theme', 'grouptheme'],
  
  // Exclusive fields
  exclusiveTo: ['exclusive to', 'exclusiveto', 'retailer', 'store exclusive'],
  exclusiveTheme: ['exclusive theme', 'exclusivetheme'],
  
  // Charity fields
  charityName: ['charity name', 'charityname', 'charity', 'organization'],
  charityCause: ['charity cause', 'charitycause', 'cause'],
  
  // Convention fields
  conventionName: ['convention name', 'conventionname', 'convention', 'con', 'expo'],
  
  // Collab fields
  collabWith: ['collab with', 'collabwith', 'collaboration', 'collab', 'collaborator'],
  collabTheme: ['collab theme', 'collabtheme'],
  
  // Set/Duo fields
  setName: ['set name', 'setname', 'duo name', 'trio name'],
  setType: ['set type', 'settype', 'duo', 'trio', 'quad'],
  
  // Re-release fields
  isRerelease: ['is rerelease', 'isrerelease', 'rerelease', 're-release'],
  originalReleaseYear: ['original release year', 'originalreleaseyear', 'original year'],
  originalReleaseSeason: ['original release season', 'originalreleaseseason', 'original season'],
  releaseNotes: ['release notes', 'releasenotes'],
  
  // Combined/parsing fields
  releaseInfo: ['release', 'release info', 'released', 'release details'],
  dateField: ['date', 'release date', 'date released', 'launch date'],
  
  // Structured tags
  baseType: ['base type', 'basetype', 'base'],
  colorFamily: ['color family', 'colorfamily', 'colour family', 'primary color', 'main color', 'hue'],
  finish: ['finish', 'finishes', 'effect', 'effects', 'texture'],
  holoType: ['holo type', 'holotype', 'holo'],
  colorShift: ['color shift', 'colorshift', 'shift', 'duochrome', 'multichrome'],
  particles: ['particles', 'glitter', 'flakes'],
  specialFeatures: ['special features', 'specialfeatures', 'special', 'features'],
  utility: ['utility', 'use', 'purpose'],
  colorCharacter: ['color character', 'colorcharacter', 'character', 'vibe'],
  
  // Status flags
  onWishlist: ['wishlist', 'wish list', 'want', 'iso', 'seeking', 'onwishlist'],
  isoPriority: ['iso priority', 'isopriority', 'priority', 'diso', 'unicorn'],
  isRareLE: ['rare', 'htf', 'hard to find', 'unicorn', 'israrele', 'limited edition', 'le'],
  
  // Destash
  isForDestash: ['destash', 'for destash', 'isfordestash', 'selling'],
  destashPrice: ['destash price', 'destashprice', 'asking price', 'sale price'],
  destashCondition: ['destash condition', 'destashcondition'],
  
  // Plate-specific
  plateType: ['plate type', 'type', 'kind'],
  plateSize: ['plate size', 'size', 'dimensions'],
  plateShape: ['plate shape', 'shape', 'form'],
  imageCount: ['image count', 'images', 'number of images', '# images', 'designs', 'patterns'],
  
  // Transfer-specific
  transferType: ['transfer type', 'decal type', 'type', 'kind'],
  
  // Embellishment-specific
  embellishmentType: ['embellishment type', 'type', 'kind', 'category'],
  color: ['color', 'colour', 'shade'],
  size: ['size', 'dimensions'],
  
  // Tool-specific
  toolType: ['tool type', 'type', 'kind', 'category'],
  material: ['material', 'made of'],
  
  // Powder-specific
  powderType: ['powder type', 'type', 'kind', 'category'],
  effect: ['effect', 'finish'],
  
  // Acrylic-specific
  acrylicType: ['acrylic type', 'type', 'kind', 'category'],
};

// ============================================
// POLISH TYPE DETECTION
// ============================================
// Valid polish types (must match app's dropdown options)
export const VALID_POLISH_TYPES = [
  'Base Coat',
  'Builder Gel',
  'Dip Powder',
  'Gel Polish',
  'Gel Stamping Polish',
  'Nail Lacquer',
  'Poly Gel',
  'Stamping Polish',
  'Top Coat',
  'Treatment',
] as const;

export type PolishType = typeof VALID_POLISH_TYPES[number];

/**
 * Detect polish type from polish name and/or brand
 * Returns the detected type or null if no match
 * 
 * Detection priority (most specific first):
 * 1. Gel Stamping Polish (must check before both "gel" and "stamping")
 * 2. Stamping Polish
 * 3. Gel Polish
 * 4. Builder Gel / BIAB
 * 5. Poly Gel
 * 6. Dip Powder
 * 7. Base Coat
 * 8. Top Coat
 * 9. Treatment
 * 10. Default: null (let user decide, or default to Nail Lacquer)
 */
export function detectPolishType(name: string, brand?: string): PolishType | null {
  const searchText = `${brand || ''} ${name}`.toLowerCase();
  
  // Gel Stamping Polish - check FIRST (before gel and stamping separately)
  if (
    /gel\s*stamp(ing)?\s*polish/i.test(searchText) ||
    /stamp(ing)?\s*gel\s*polish/i.test(searchText) ||
    /gel\s*stamp/i.test(searchText)
  ) {
    return 'Gel Stamping Polish';
  }
  
  // Stamping Polish
  if (
    /stamp(ing)?\s*polish/i.test(searchText) ||
    /polish.*stamp(ing)?/i.test(searchText) ||
    // Brand-specific stamping indicators
    /maniology.*stamping/i.test(searchText) ||
    /born\s*pretty.*stamping/i.test(searchText) ||
    /moyou.*stamping/i.test(searchText)
  ) {
    return 'Stamping Polish';
  }
  
  // Builder Gel / BIAB
  if (
    /builder\s*gel/i.test(searchText) ||
    /\bbiab\b/i.test(searchText) ||
    /builder\s*in\s*a\s*bottle/i.test(searchText) ||
    /building\s*gel/i.test(searchText)
  ) {
    return 'Builder Gel';
  }
  
  // Poly Gel
  if (
    /poly\s*gel/i.test(searchText) ||
    /polygel/i.test(searchText) ||
    /acrygel/i.test(searchText)
  ) {
    return 'Poly Gel';
  }
  
  // Gel Polish (check after more specific gel types)
  if (
    /gel\s*polish/i.test(searchText) ||
    /gel\s*lacquer/i.test(searchText) ||
    /soak\s*off\s*gel/i.test(searchText) ||
    /uv\s*gel/i.test(searchText) ||
    /led\s*gel/i.test(searchText) ||
    /shellac/i.test(searchText) ||
    /gelish/i.test(searchText)
  ) {
    return 'Gel Polish';
  }
  
  // Dip Powder
  if (
    /dip\s*powder/i.test(searchText) ||
    /dipping\s*powder/i.test(searchText) ||
    /sns\s*powder/i.test(searchText) ||
    /\bdip\b.*nail/i.test(searchText)
  ) {
    return 'Dip Powder';
  }
  
  // Base Coat
  if (
    /base\s*coat/i.test(searchText) ||
    /basecoat/i.test(searchText) ||
    /bonder/i.test(searchText) ||
    /ridge\s*fill(er|ing)?/i.test(searchText) ||
    /sticky\s*base/i.test(searchText)
  ) {
    return 'Base Coat';
  }
  
  // Top Coat
  if (
    /top\s*coat/i.test(searchText) ||
    /topcoat/i.test(searchText) ||
    /glossy?\s*top/i.test(searchText) ||
    /matte\s*top/i.test(searchText) ||
    /quick\s*dry/i.test(searchText) ||
    /fast\s*dry/i.test(searchText) ||
    /sealer/i.test(searchText)
  ) {
    return 'Top Coat';
  }
  
  // Treatment
  if (
    /treatment/i.test(searchText) ||
    /strengthener/i.test(searchText) ||
    /hardener/i.test(searchText) ||
    /nail\s*repair/i.test(searchText) ||
    /cuticle/i.test(searchText) ||
    /nail\s*oil/i.test(searchText) ||
    /growth\s*treatment/i.test(searchText) ||
    /keratin/i.test(searchText) ||
    /calcium/i.test(searchText)
  ) {
    return 'Treatment';
  }
  
  // No specific type detected - return null (will default to Nail Lacquer or let user decide)
  return null;
}

/**
 * Normalize polish type input to valid type
 * Handles variations like "gel", "lacquer", "regular" etc.
 */
export function normalizePolishType(input: string): PolishType | null {
  const normalized = input.toLowerCase().trim();
  
  // Direct matches (case-insensitive)
  const directMatch = VALID_POLISH_TYPES.find(
    type => type.toLowerCase() === normalized
  );
  if (directMatch) return directMatch;
  
  // Common variations
  if (/^(nail\s*)?lacquer$/i.test(normalized) || /^regular$/i.test(normalized) || /^normal$/i.test(normalized)) {
    return 'Nail Lacquer';
  }
  if (/^gel$/i.test(normalized)) {
    return 'Gel Polish';
  }
  if (/^stamp(ing)?$/i.test(normalized)) {
    return 'Stamping Polish';
  }
  if (/^gel\s*stamp(ing)?$/i.test(normalized)) {
    return 'Gel Stamping Polish';
  }
  if (/^builder$/i.test(normalized) || /^biab$/i.test(normalized)) {
    return 'Builder Gel';
  }
  if (/^poly$/i.test(normalized)) {
    return 'Poly Gel';
  }
  if (/^dip$/i.test(normalized)) {
    return 'Dip Powder';
  }
  if (/^base$/i.test(normalized)) {
    return 'Base Coat';
  }
  if (/^top$/i.test(normalized)) {
    return 'Top Coat';
  }
  
  return null;
}

export interface ParsedCSV {
  headers: string[];
  rows: string[][];
  rowCount: number;
}

export interface ColumnMapping {
  [csvColumn: string]: string | null;  // Maps to any field key
}

export interface MappedRow {
  brand: string;
  name: string;
  category?: string;
  colorDescription?: string;

  // Collection
  collection?: string;
  collectionName?: string;

  // Release system (match app's data model)
  releaseType?: string;
  releaseYear?: number;
  releaseSeason?: string;
  releaseMonth?: number;                  // 1-12, more precise than season
  releaseNotes?: string;

  // Event fields
  eventSource?: string;
  eventTheme?: string;

  // Box fields
  boxName?: string;
  boxTheme?: string;

  // Group fields
  groupName?: string;
  groupTheme?: string;

  // Exclusive fields
  exclusiveTo?: string;
  exclusiveTheme?: string;

  // Charity fields
  charityName?: string;
  charityCause?: string;

  // Convention fields
  conventionName?: string;

  // Collab fields
  collabWith?: string;
  collabTheme?: string;

  // Set fields
  setName?: string;
  setType?: string;

  // Rerelease info
  isRerelease?: boolean;
  originalReleaseYear?: number;
  originalReleaseSeason?: string;

  // Polish-specific fields
  polishType?: string;
  baseType?: string;
  colorShift?: string;

  // Tag arrays
  holoType?: string[];
  particles?: string[];
  specialFeatures?: string[];
  colorCharacter?: string[];
  utility?: string[];

  // Plate-specific fields
  plateType?: string;
  plateSize?: string;
  plateShape?: string;
  imageCount?: string;

  // Transfer-specific fields
  transferType?: string;

  // Embellishment-specific fields
  embellishmentType?: string;
  color?: string;
  size?: string;

  // Tool-specific fields
  toolType?: string;
  material?: string;

  // Powder-specific fields
  powderType?: string;
  effect?: string;

  // Acrylic-specific fields
  acrylicType?: string;

  // Other fields
  quantity?: number;
  notes?: string;
  publicNotes?: string;
  colorFamily?: string[];
  finish?: string[];
  tags?: string[];
  isFavorite?: boolean;
  onWishlist?: boolean;
  isRareLE?: boolean;
  hasSwatched?: boolean;
  swatched?: boolean;
  isForDestash?: boolean;
  price?: number;
  destashPrice?: number;
  destashCondition?: string;
  storage?: string;
  photoUrl?: string;
  barcode?: string;
  timesUsed?: number;
  lastUsedDate?: string;
  isoPriority?: string;
  line?: string;
  swatchNumber?: string;

  _rowIndex: number; // For error tracking
  _errors: string[]; // Validation errors
}

function parseDateFromText(value: string): { date: string | null; text: string } {
  if (!value) return { date: null, text: '' };
  
  // Pattern 1: "2020/01: Text" or "2020/01 - Text" or "2020-01: Text"
  const pattern1 = /^(\d{4}[\/\-]\d{1,2})\s*[:\-–—\uFFFD]?\s*(.*)$/;
  const match1 = value.match(pattern1);
  if (match1) {
    return { date: match1[1], text: match1[2].trim() };
  }
  
  // Pattern 2: "Text – 2022/07: More Text" or "Text � 2022/07: More Text" (date in middle)
  // Include \uFFFD (replacement char), –—- (dashes), ◆◇♦ (diamonds) as separators
  const pattern2 = /^(.*?)\s*[–—\-\uFFFD◆◇♦]\s*(\d{4}[\/\-]\d{1,2})\s*[:\-–—\uFFFD]?\s*(.*)$/;
  const match2 = value.match(pattern2);
  if (match2) {
    const text = [match2[1], match2[3]].filter(Boolean).join(': ').trim();
    return { date: match2[2], text };
  }
  
  // Pattern 3: "Jan 2020" or "January 2020" or "2020"
  const monthNames = 'jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|january|february|march|april|may|june|july|august|september|october|november|december';
  const pattern3 = new RegExp(`((?:${monthNames})\\s*\\d{4}|\\d{4})`, 'i');
  const match3 = value.match(pattern3);
  if (match3) {
    const date = match3[1];
    const text = value.replace(date, '').replace(/^[\s:\-–—\uFFFD]+|[\s:\-–—\uFFFD]+$/g, '').trim();
    return { date, text: text || value };
  }
  
  // No date found, return original text
  return { date: null, text: value };
}


export function detectDelimiter(text: string): string {
  const firstLine = text.split('\n')[0] || '';
  
  const delimiters = [',', '\t', ';', '|'];
  const counts = delimiters.map(d => ({
    delimiter: d,
    count: (firstLine.match(new RegExp(`\\${d}`, 'g')) || []).length
  }));
  
  // Return the delimiter with the highest count
  counts.sort((a, b) => b.count - a.count);
  return counts[0]?.count > 0 ? counts[0].delimiter : ',';
}

export function parseCSVString(text: string, delimiter: string = ','): ParsedCSV {
  const lines = text.split(/\r?\n/).filter(line => line.trim());
  
  if (lines.length === 0) {
    return { headers: [], rows: [], rowCount: 0 };
  }

  // Parse with proper quote handling
  const parseRow = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];
      
      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote
          current += '"';
          i++;
        } else {
          // Toggle quote mode
          inQuotes = !inQuotes;
        }
      } else if (char === delimiter && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    // Don't forget the last field
    result.push(current.trim());
    
    return result;
  };

  const headers = parseRow(lines[0]);
  const rows = lines.slice(1).map(line => parseRow(line));
  
  return {
    headers,
    rows,
    rowCount: rows.length
  };
}

function normalizeText(text: string): string {
  return text
    // Smart quotes to regular quotes
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")  // Single quotes
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')  // Double quotes
    // Fancy dashes and separators to regular dashes
    .replace(/[\u2013\u2014\u2015]/g, '-')        // En-dash, em-dash, horizontal bar
    .replace(/[\u25C6\u25C7\u25CA\u2666]/g, '-')  // Diamond shapes ◆ ◇ ◊ ♦
    .replace(/[\u2022\u2023\u25E6\u2043]/g, '-')  // Bullet points • ‣ ◦ ⁃
    .replace(/[\u00B7\u2027]/g, '-')              // Middle dots · ‧
    // Other special characters
    .replace(/\u2026/g, '...')                     // Ellipsis
    .replace(/[\u00A0]/g, ' ')                     // Non-breaking space
    // Handle replacement character contextually:
    // - If surrounded by whitespace, it's a separator -> convert to dash
    // - If between word characters, it's likely an apostrophe
    .replace(/\s\uFFFD\s/g, ' - ')                // Separator context
    .replace(/(\w)\uFFFD(\w)/g, "$1'$2")          // Mid-word apostrophe (Bob's)
    .replace(/\uFFFD/g, "'");                      // Default to apostrophe for remaining
}

export async function parseCSV(file: File): Promise<ParsedCSV> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        let text = e.target?.result as string;
        // Normalize special characters
        text = normalizeText(text);
        const delimiter = detectDelimiter(text);
        const result = parseCSVString(text, delimiter);
        resolve(result);
      } catch (error) {
        reject(new Error('Failed to parse CSV file'));
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file, 'UTF-8');
  });
}

export function autoMapColumns(headers: string[], itemType: ImportItemType = 'polish'): ColumnMapping {
  const mapping: ColumnMapping = {};
  const validFields = getFieldsForType(itemType);
  const validFieldKeys = Object.keys(validFields);
  
  for (const header of headers) {
    const normalizedHeader = header.toLowerCase().trim();
    
    // Check each field's aliases
    for (const [field, aliases] of Object.entries(COLUMN_ALIASES)) {
      // Only map fields that are valid for this item type
      if (!validFieldKeys.includes(field)) continue;
      
      if (aliases.some(alias => 
        normalizedHeader === alias || 
        normalizedHeader.includes(alias) ||
        alias.includes(normalizedHeader)
      )) {
        mapping[header] = field;
        break;
      }
    }
    
    // If no match found, set to null
    if (!(header in mapping)) {
      mapping[header] = null;
    }
  }
  
  return mapping;
}

// Release type keywords - mapped to dropdown values in PolishDetailModal
// Dropdown values: "unknown", "event", "collection", "core"
const RELEASE_TYPE_KEYWORDS: { pattern: RegExp; type: string }[] = [
  // Maps to "core"
  { pattern: /\bcore\b|permanent/i, type: 'core' },
  // Maps to "event"  
  { pattern: /\bevent\b|box\b|ppu\b|polish\s*pick\s*up|hella|hhc|indie\s*expo/i, type: 'event' },
  // Maps to "collection"
  { pattern: /\bcollection\b|release|seasonal|limited|le\b|exclusive/i, type: 'collection' },
];

function extractReleaseType(value: string): string | undefined {
  for (const { pattern, type } of RELEASE_TYPE_KEYWORDS) {
    if (pattern.test(value)) {
      return type;
    }
  }
  return undefined;
}

function normalizeReleaseType(value: string): string {
  const lower = value.toLowerCase().trim();
  
  // Direct matches to dropdown values
  if (lower === 'core' || lower === 'permanent') return 'core';
  if (lower === 'event' || lower === 'box') return 'event';
  if (lower === 'collection') return 'collection';
  if (lower === 'unknown') return 'unknown';
  
  // Try pattern matching
  const extracted = extractReleaseType(value);
  if (extracted) return extracted;
  
  // Default to collection for LE/limited/exclusive since those are typically collection releases
  if (/le\b|limited|exclusive|seasonal/i.test(lower)) return 'collection';
  
  return 'unknown';
}

function parseBoolean(value: string | undefined): boolean | undefined {
  if (!value) return undefined;
  const normalized = value.toLowerCase().trim();
  if (['yes', 'true', '1', 'y', 'x', '✓', '✔'].includes(normalized)) return true;
  if (['no', 'false', '0', 'n', ''].includes(normalized)) return false;
  return undefined;
}

// Maps common imported finish values to valid FINISH_OPTIONS entries
const FINISH_ALIASES: Record<string, Finish> = {
  // Casing variants
  'glossy': 'glossy',
  'Glossy': 'glossy',
  'shiny': 'glossy',
  'Shiny': 'glossy',
  'creme': 'glossy',
  'cream': 'glossy',
  'crème': 'glossy',
  // Shimmer variants
  'shimmer': 'shimmer',
  'Shimmer': 'shimmer',
  'shimmery': 'shimmer',
  // Metallic variants
  'metallic': 'metallic',
  'Metallic': 'metallic',
  'foil': 'metallic',
  // Chrome variants
  'chrome': 'chrome',
  'Chrome': 'chrome',
  'mirror': 'chrome',
  // Matte variants
  'matte': 'matte',
  'Matte': 'matte',
  'flat': 'matte',
  // Satin variants
  'satin': 'satin',
  'Satin': 'satin',
  'velvet': 'satin',
  // Frost variants
  'frost': 'frost',
  'Frost': 'frost',
  'frosted': 'frost',
  // Iridescent variants
  'iridescent': 'iridescent',
  'Iridescent': 'iridescent',
  'duochrome': 'iridescent',
  'multichrome': 'iridescent',
  // Glass variants
  'glass': 'glass',
  'Glass': 'glass',
  'jelly': 'glass',
  // Holographic variants
  'holographic': 'holographic',
  'Holographic': 'holographic',
  'holo': 'holographic',
  'Holo': 'holographic',
  'holo magnetic': 'holographic',
  'Holo Magnetic': 'holographic',
  'holo/holographic': 'holographic',
};

function normalizeFinish(values: string[]): Finish[] {
  const result: Finish[] = [];
  for (const val of values) {
    const trimmed = val.trim();
    // Check alias map first
    if (FINISH_ALIASES[trimmed]) {
      result.push(FINISH_ALIASES[trimmed]);
      continue;
    }
    // Try case-insensitive match against FINISH_OPTIONS directly
    const lower = trimmed.toLowerCase();
    const match = FINISH_OPTIONS.find((opt) => opt.toLowerCase() === lower);
    if (match) {
      result.push(match);
    }
    // If no match found, silently drop the value — don't save invalid data
  }
  // Remove duplicates (e.g. two aliases mapping to same value)
  return [...new Set(result)];
}

function parseArray(value: string | undefined): string[] | undefined {
  if (!value) return undefined;
  return value.split(/[,;]/).map(v => v.trim()).filter(v => v.length > 0);
}

function extractYear(dateStr: string): number | undefined {
  const yearMatch = dateStr.match(/\b(19|20)\d{2}\b/);
  if (yearMatch) {
    return parseInt(yearMatch[0], 10);
  }
  return undefined;
}

function extractSeason(value: string): string | undefined {
  const lower = value.toLowerCase();
  
  // First try to match month numbers in date format (more precise)
  const monthMatch = value.match(/[\/\-](\d{1,2})(?:[\/\-]|$)/);
  if (monthMatch) {
    const month = parseInt(monthMatch[1], 10);
    if (month >= 3 && month <= 5) return 'Spring';
    if (month >= 6 && month <= 8) return 'Summer';
    if (month >= 9 && month <= 11) return 'Fall';
    if (month === 12 || month === 1 || month === 2) return 'Winter';
  }
  
  // Then try month names
  if (/\b(mar|apr|may|march|april)\b/i.test(lower)) return 'Spring';
  if (/\b(jun|jul|aug|june|july|august)\b/i.test(lower)) return 'Summer';
  if (/\b(sep|oct|nov|september|october|november)\b/i.test(lower)) return 'Fall';
  if (/\b(dec|jan|feb|december|january|february)\b/i.test(lower)) return 'Winter';
  
  // Then try season names directly
  if (/\bspring\b/i.test(lower)) return 'Spring';
  if (/\bsummer\b/i.test(lower)) return 'Summer';
  if (/\b(fall|autumn)\b/i.test(lower)) return 'Fall';
  if (/\bwinter\b/i.test(lower)) return 'Winter';
  
  return undefined;
}

/**
 * Extract month number (1-12) from a date string
 * Returns undefined if no month found
 */
function extractMonth(value: string): number | undefined {
  const lower = value.toLowerCase();
  
  // First try to match month numbers in date format (more precise)
  // Handles: 2024/03, 03/15/2024, 2024-03-15, etc.
  const monthMatch = value.match(/[\/\-](\d{1,2})(?:[\/\-]|$)/);
  if (monthMatch) {
    const month = parseInt(monthMatch[1], 10);
    if (month >= 1 && month <= 12) return month;
  }
  
  // Try month names
  if (/\b(jan|january)\b/i.test(lower)) return 1;
  if (/\b(feb|february)\b/i.test(lower)) return 2;
  if (/\b(mar|march)\b/i.test(lower)) return 3;
  if (/\b(apr|april)\b/i.test(lower)) return 4;
  if (/\bmay\b/i.test(lower)) return 5;
  if (/\b(jun|june)\b/i.test(lower)) return 6;
  if (/\b(jul|july)\b/i.test(lower)) return 7;
  if (/\b(aug|august)\b/i.test(lower)) return 8;
  if (/\b(sep|sept|september)\b/i.test(lower)) return 9;
  if (/\b(oct|october)\b/i.test(lower)) return 10;
  if (/\b(nov|november)\b/i.test(lower)) return 11;
  if (/\b(dec|december)\b/i.test(lower)) return 12;
  
  return undefined;
}

function detectEventSource(value: string): string | undefined {
  const lower = value.toLowerCase();
  if (/polish\s*pick\s*up|ppu/i.test(lower)) return 'Polish Pickup';
  // Handle "Helga" typo for "Hella"
  if (/hel[lg]a\s*handmade|hhc/i.test(lower)) return 'Hella Handmade Creations';
  if (/indie\s*expo/i.test(lower)) return 'Indie Expo';
  if (/polish\s*con/i.test(lower)) return 'Polish Con';
  if (/what.?s\s*in.?die/i.test(lower)) return "What's Indie Box";
  if (/femme\s*fatale/i.test(lower)) return 'Femme Fatale';
  if (/enchanted\s*polish/i.test(lower)) return 'Enchanted Polish';
  if (/illyrian/i.test(lower)) return 'Illyrian Polish';
  if (/monthly\s*box/i.test(lower)) return 'Monthly Box';
  if (/box\s*exclusive/i.test(lower)) return 'Box Exclusive';
  if (/subscription\s*box/i.test(lower)) return 'Subscription Box';
  if (/anniversary/i.test(lower)) return 'Anniversary';
  if (/birthday/i.test(lower)) return 'Birthday';
  if (/black\s*friday/i.test(lower)) return 'Black Friday';
  if (/cyber\s*monday/i.test(lower)) return 'Cyber Monday';
  if (/christmas|xmas|holiday\s*release/i.test(lower)) return 'Holiday';
  if (/valentine/i.test(lower)) return 'Valentine';
  if (/halloween/i.test(lower)) return 'Halloween';
  if (/collab|collaboration/i.test(lower)) return 'Collaboration';
  if (/\bbox\b/i.test(lower)) return 'Box'; // Generic "box" reference
  if (/polished\s*gamers/i.test(lower)) return 'Polished Gamers Box';
  if (/for\s*the\s*love\s*of\s*polish/i.test(lower)) return 'For the Love of Polish Box';
  if (/hues\s*in.?die/i.test(lower)) return 'Hues In-die Box';
  if (/indie\s*galactic/i.test(lower)) return 'Indie Galactic Box';
  if (/polish\s*palette/i.test(lower)) return 'The Polish Palette';
  if (/anonamaker/i.test(lower)) return 'Anonamaker Box';
  if (/custom\s*[–—\-\uFFFD]/i.test(lower)) return 'Custom';
  if (/exclusive\s*to/i.test(lower)) return 'Exclusive';
  return undefined;
}

function getEventRemovalPattern(eventName: string): RegExp {
  // Create patterns that match the event name with flexible spacing/punctuation
  const patterns: { [key: string]: RegExp } = {
    'Polish Pickup': /polish\s*pick\s*-?\s*up/gi,
    'Hella Handmade Creations': /hel[lg]a\s*handmade\s*(creations?)?/gi,
    'Indie Expo': /indie\s*expo/gi,
    'Polish Con': /polish\s*con/gi,
    "What's Indie Box": /what'?s?\s*in-?die\s*box/gi,
    'Femme Fatale': /femme\s*fatale/gi,
    'Enchanted Polish': /enchanted\s*polish/gi,
    'Illyrian Polish': /illyrian\s*(polish)?/gi,
    'Monthly Box': /monthly\s*box/gi,
    'Box Exclusive': /box\s*exclusive/gi,
    'Subscription Box': /subscription\s*box/gi,
  };
  return patterns[eventName] || new RegExp(eventName.replace(/\s+/g, '\\s*'), 'gi');
}

function cleanExtractedText(text: string): string {
  return text
    // Remove any remaining date fragments
    .replace(/\d{4}[\/\-]\d{1,2}/g, '')      // Full dates like 2018/07
    .replace(/[\/\-]\d{1,2}\s*:/g, ':')      // Partial dates like /07:
    .replace(/[\/\-]\d{1,2}/g, '')           // Partial dates like /07
    .replace(/^\d{4}\s*/g, '')               // Year at start
    .replace(/\s*\d{4}$/g, '')               // Year at end
    // Remove specific problematic patterns
    .replace(/^'\s*:\s*/g, '')               // Leading ': pattern
    .replace(/^'\s+/g, '')                   // Leading ' followed by whitespace  
    .replace(/^:\s*/g, '')                   // Leading : pattern
    // Remove leftover punctuation at start (but preserve apostrophes that start words like 'Tis)
    .replace(/^[\s\"\-–—:,;\.◆◇♦•·]+/g, '') // Leading punctuation (NOT apostrophe)
    .replace(/[\s'\"\-–—:,;\.◆◇♦•·]+$/g, '')// Trailing punctuation (including apostrophe)
    // Clean up spaces around colons/dashes that might be left
    .replace(/\s*[:\-–—]\s*$/g, '')          // Trailing separators
    .replace(/^\s*[:\-–—]\s*/g, '')          // Leading separators
    // Clean up multiple spaces
    .replace(/\s+/g, ' ')
    .trim();
}

export function applyMapping(
  headers: string[],
  rows: string[][],
  mapping: ColumnMapping
): MappedRow[] {
  return rows.map((row, rowIndex) => {
    const mapped: MappedRow = {
      brand: '',
      name: '',
      _rowIndex: rowIndex + 2, // +2 for 1-indexed and header row
      _errors: []
    };
    
    // Apply mapping for each column
    headers.forEach((header, colIndex) => {
      const field = mapping[header];
      const value = row[colIndex]?.trim() || '';
      
      if (!field || !value) return;
      
      switch (field) {
        // Simple text fields
        case 'brand':
        case 'name':
        case 'category':
        case 'colorDescription':
        case 'notes':
        case 'publicNotes':
        case 'photoUrl':
          mapped[field] = value;
          break;
        
        // ============================================
        // COLLECTION - Now uses smart parser!
        // ============================================
        case 'collection':
          // Use the new smart parser to extract components
          if (needsParsing(value)) {
            const parsed = applyParsedCollection(value, {
              collectionName: mapped.collection,
              eventName: mapped.eventSource,
              eventTheme: mapped.eventTheme,
              releaseYear: mapped.releaseYear,
              releaseSeason: mapped.releaseSeason,
              releaseType: mapped.releaseType,
            });

            // Check if this is actually a tag (e.g., "MYTHICAL-Unicorn")
            if (parsed.parseType === 'tag') {
              // Split on hyphen and add to tags array
              const newTags = value.split('-').map(t => t.trim()).filter(t => t.length > 0);
              mapped.tags = [...(mapped.tags || []), ...newTags];
              // Don't set collection name - it was a tag, not a collection
            } else {
              // Apply parsed values normally
              if (parsed.collectionName && !mapped.collection) mapped.collection = parsed.collectionName;
              if (parsed.eventName && !mapped.eventSource) mapped.eventSource = parsed.eventName;
              if (parsed.eventTheme && !mapped.eventTheme) mapped.eventTheme = parsed.eventTheme;
              if (parsed.releaseYear && !mapped.releaseYear) mapped.releaseYear = parsed.releaseYear;
              if (parsed.releaseSeason && !mapped.releaseSeason) mapped.releaseSeason = parsed.releaseSeason;
              if (parsed.releaseMonth && !mapped.releaseMonth) mapped.releaseMonth = parsed.releaseMonth;
              if (parsed.releaseType && !mapped.releaseType) mapped.releaseType = parsed.releaseType;
            }
          } else {
            // No parsing needed, just use the value as collection name
            mapped.collection = value;
            if (!mapped.releaseType) {
              mapped.releaseType = 'collection';
            }
          }
          break;
        
        // ============================================
        // COLLECTION INFO - Combined field, uses smart parser
        // ============================================
        case 'collectionInfo':
          // Use the new smart parser
          if (needsParsing(value)) {
            const parsed = applyParsedCollection(value, {
              collectionName: mapped.collection,
              eventName: mapped.eventSource,
              eventTheme: mapped.eventTheme,
              releaseYear: mapped.releaseYear,
              releaseSeason: mapped.releaseSeason,
              releaseType: mapped.releaseType,
            });

            // Check if this is actually a tag (e.g., "MYTHICAL-Unicorn")
            if (parsed.parseType === 'tag') {
              // Split on hyphen and add to tags array
              const newTags = value.split('-').map(t => t.trim()).filter(t => t.length > 0);
              mapped.tags = [...(mapped.tags || []), ...newTags];
              // Don't set collection name - it was a tag, not a collection
            } else {
              // Apply parsed values normally
              if (parsed.collectionName && !mapped.collection) mapped.collection = parsed.collectionName;
              if (parsed.eventName && !mapped.eventSource) mapped.eventSource = parsed.eventName;
              if (parsed.eventTheme && !mapped.eventTheme) mapped.eventTheme = parsed.eventTheme;
              if (parsed.releaseYear && !mapped.releaseYear) mapped.releaseYear = parsed.releaseYear;
              if (parsed.releaseSeason && !mapped.releaseSeason) mapped.releaseSeason = parsed.releaseSeason;
              if (parsed.releaseMonth && !mapped.releaseMonth) mapped.releaseMonth = parsed.releaseMonth;
              if (parsed.releaseType && !mapped.releaseType) mapped.releaseType = parsed.releaseType;
            }
          } else {
            // Fallback to old date parsing
            const collParsed = parseDateFromText(value);
            let collectionNameValue = collParsed.text || value;
            collectionNameValue = cleanExtractedText(collectionNameValue);
            
            if (collectionNameValue && !mapped.collection) {
              mapped.collection = collectionNameValue;
            }
            if (collParsed.date && !mapped.releaseYear) {
              mapped.releaseYear = extractYear(collParsed.date);
            }
            if (collParsed.date && !mapped.releaseSeason) {
              mapped.releaseSeason = extractSeason(collParsed.date);
            }
            if (collParsed.date && !mapped.releaseMonth) {
              mapped.releaseMonth = extractMonth(collParsed.date);
            }
            if (!mapped.releaseType) {
              mapped.releaseType = 'collection';
            }
          }
          break;
        
        // Release type (standalone or needs parsing) - maps to dropdown values
        case 'releaseType':
          mapped.releaseType = normalizeReleaseType(value);
          break;
        
        // Release year (standalone)
        case 'releaseYear':
          const yearNum = extractYear(value) || parseInt(value, 10);
          if (!isNaN(yearNum) && yearNum > 1900 && yearNum < 2100) {
            mapped.releaseYear = yearNum;
          }
          break;
        
        // Release season (standalone)  
        case 'releaseSeason':
          mapped.releaseSeason = extractSeason(value) || value;
          // Also try to extract month if present
          if (!mapped.releaseMonth) {
            mapped.releaseMonth = extractMonth(value);
          }
          break;
        
        // Event source (standalone - just the event name)
        case 'eventSource':
          // Try to detect known event, otherwise use the value
          mapped.eventSource = detectEventSource(value) || value;
          // Also set releaseType to event
          if (!mapped.releaseType) {
            mapped.releaseType = 'event';
          }
          break;
        
        // Event theme (standalone)
        case 'eventTheme':
          mapped.eventTheme = value;
          // Also set releaseType to event
          if (!mapped.releaseType) {
            mapped.releaseType = 'event';
          }
          break;
        
        // ============================================
        // RELEASE INFO - Combined, uses smart parser
        // ============================================
        case 'releaseInfo':
          // Use the new smart parser first
          if (needsParsing(value)) {
            const parsed = applyParsedCollection(value, {
              collectionName: mapped.collection,
              eventName: mapped.eventSource,
              eventTheme: mapped.eventTheme,
              releaseYear: mapped.releaseYear,
              releaseSeason: mapped.releaseSeason,
              releaseType: mapped.releaseType,
            });

            // Check if this is actually a tag
            if (parsed.parseType === 'tag') {
              const newTags = value.split('-').map(t => t.trim()).filter(t => t.length > 0);
              mapped.tags = [...(mapped.tags || []), ...newTags];
            } else {
              // Apply parsed values normally
              if (parsed.collectionName && !mapped.collection) mapped.collection = parsed.collectionName;
              if (parsed.eventName && !mapped.eventSource) mapped.eventSource = parsed.eventName;
              if (parsed.eventTheme && !mapped.eventTheme) mapped.eventTheme = parsed.eventTheme;
              if (parsed.releaseYear && !mapped.releaseYear) mapped.releaseYear = parsed.releaseYear;
              if (parsed.releaseSeason && !mapped.releaseSeason) mapped.releaseSeason = parsed.releaseSeason;
              if (parsed.releaseMonth && !mapped.releaseMonth) mapped.releaseMonth = parsed.releaseMonth;
              if (parsed.releaseType && !mapped.releaseType) mapped.releaseType = parsed.releaseType;
            }
          } else {
            // Fallback to old parsing logic
            const releaseParsed = parseDateFromText(value);
            const isEventRelease = detectEventSource(value);
            
            if (isEventRelease) {
              if (!mapped.releaseType) mapped.releaseType = 'event';
              if (!mapped.eventSource) mapped.eventSource = isEventRelease;
              
              if (releaseParsed.text && !mapped.eventTheme) {
                let theme = releaseParsed.text;
                const removalPattern = getEventRemovalPattern(isEventRelease);
                theme = theme.replace(removalPattern, '');
                theme = cleanExtractedText(theme);
                if (theme) mapped.eventTheme = theme;
              }
            } else {
              const extractedType = extractReleaseType(value);
              if (extractedType && !mapped.releaseType) {
                mapped.releaseType = extractedType;
              }
              
              if (mapped.releaseType === 'collection' && releaseParsed.text) {
                let collName = releaseParsed.text;
                collName = collName.replace(/\b(le|limited|edition|exclusive|seasonal|collection)\b/gi, '');
                collName = cleanExtractedText(collName);
                if (collName && !mapped.collection) {
                  mapped.collection = collName;
                }
              }
            }
            
            if (releaseParsed.date && !mapped.releaseYear) {
              mapped.releaseYear = extractYear(releaseParsed.date);
            }
            if (releaseParsed.date && !mapped.releaseSeason) {
              mapped.releaseSeason = extractSeason(releaseParsed.date);
            }
            if (releaseParsed.date && !mapped.releaseMonth) {
              mapped.releaseMonth = extractMonth(releaseParsed.date);
            }
          }
          break;
        
        // ============================================
        // EVENT INFO - Combined, uses smart parser
        // ============================================
        case 'eventInfo':
          // Use the new smart parser first
          if (needsParsing(value)) {
            const parsed = applyParsedCollection(value, {
              collectionName: mapped.collection,
              eventName: mapped.eventSource,
              eventTheme: mapped.eventTheme,
              releaseYear: mapped.releaseYear,
              releaseSeason: mapped.releaseSeason,
              releaseType: mapped.releaseType,
            });

            // Check if this is actually a tag
            if (parsed.parseType === 'tag') {
              const newTags = value.split('-').map(t => t.trim()).filter(t => t.length > 0);
              mapped.tags = [...(mapped.tags || []), ...newTags];
            } else {
              // Apply parsed values normally
              if (parsed.collectionName && !mapped.collection) mapped.collection = parsed.collectionName;
              if (parsed.eventName && !mapped.eventSource) mapped.eventSource = parsed.eventName;
              if (parsed.eventTheme && !mapped.eventTheme) mapped.eventTheme = parsed.eventTheme;
              if (parsed.releaseYear && !mapped.releaseYear) mapped.releaseYear = parsed.releaseYear;
              if (parsed.releaseSeason && !mapped.releaseSeason) mapped.releaseSeason = parsed.releaseSeason;
              if (parsed.releaseMonth && !mapped.releaseMonth) mapped.releaseMonth = parsed.releaseMonth;
              if (!mapped.releaseType) mapped.releaseType = 'event';
            }
          } else {
            // Fallback to old parsing logic
            const eventParsed = parseDateFromText(value);
            const detectedEvent = detectEventSource(value);
            if (detectedEvent && !mapped.eventSource) {
              mapped.eventSource = detectedEvent;
            } else if (!mapped.eventSource) {
              mapped.eventSource = eventParsed.text || value;
            }
            if (eventParsed.text && !mapped.eventTheme) {
              let theme = eventParsed.text;
              if (detectedEvent) {
                const removalPattern = getEventRemovalPattern(detectedEvent);
                theme = theme.replace(removalPattern, '');
              }
              theme = cleanExtractedText(theme);
              
              if (theme && theme !== mapped.eventSource) mapped.eventTheme = theme;
            }
            if (eventParsed.date && !mapped.releaseYear) {
              mapped.releaseYear = extractYear(eventParsed.date);
            }
            if (eventParsed.date && !mapped.releaseSeason) {
              mapped.releaseSeason = extractSeason(eventParsed.date);
            }
            if (eventParsed.date && !mapped.releaseMonth) {
              mapped.releaseMonth = extractMonth(eventParsed.date);
            }
            if (!mapped.releaseType) {
              mapped.releaseType = 'event';
            }
          }
          break;
        
        // Standalone date field - extract year, season, and month
        case 'dateField':
          const dateYear = extractYear(value);
          if (dateYear && !mapped.releaseYear) {
            mapped.releaseYear = dateYear;
          }
          const dateSeason = extractSeason(value);
          if (dateSeason && !mapped.releaseSeason) {
            mapped.releaseSeason = dateSeason;
          }
          const dateMonth = extractMonth(value);
          if (dateMonth && !mapped.releaseMonth) {
            mapped.releaseMonth = dateMonth;
          }
          break;
        
        // Quantity
        case 'quantity':
          const num = parseInt(value, 10);
          if (!isNaN(num) && num > 0) {
            mapped.quantity = num;
          }
          break;
        
        // Price (decimal number)
        case 'price':
          // Remove currency symbols and parse as float
          const cleanPrice = value.replace(/[$£€,]/g, '').trim();
          const priceNum = parseFloat(cleanPrice);
          if (!isNaN(priceNum) && priceNum >= 0) {
            mapped.price = priceNum;
          }
          break;
        
        // Storage location (simple string)
        case 'storage':
          if (value.trim()) {
            mapped.storage = value.trim();
          }
          break;
        
        // Array fields - structured tags
        case 'colorFamily':
        case 'holoType':
        case 'particles':
        case 'specialFeatures':
        case 'utility':
        case 'colorCharacter':
          mapped[field] = parseArray(value);
          break;

        case 'finish': {
          const parsed = parseArray(value);
          if (parsed && parsed.length > 0) {
            const normalized = normalizeFinish(parsed);
            if (normalized.length > 0) mapped.finish = normalized;
          }
          break;
        }
        
        // Free-form tags
        case 'tags':
          mapped.tags = parseArray(value);
          break;
        
        // Boolean flags
        case 'isFavorite':
        case 'onWishlist':
        case 'isRareLE':
        case 'swatched':
        case 'hasSwatched':
        case 'isRerelease':
        case 'isForDestash':
          mapped[field] = parseBoolean(value);
          break;
        
        // Numeric fields
        case 'timesUsed':
        case 'originalReleaseYear':
          const intVal = parseInt(value, 10);
          if (!isNaN(intVal)) {
            mapped[field] = intVal;
          }
          break;
        
        case 'destashPrice':
          const destashNum = parseFloat(value.replace(/[$£€,]/g, '').trim());
          if (!isNaN(destashNum) && destashNum >= 0) {
            mapped.destashPrice = destashNum;
          }
          break;
        
        // Polish Type - normalize to valid values
        case 'polishType':
          if (value.trim()) {
            const normalized = normalizePolishType(value.trim());
            if (normalized) {
              mapped.polishType = normalized;
            } else {
              // Keep original value if can't normalize - user can fix in review
              mapped.polishType = value.trim();
            }
          }
          break;
        
        // String fields - Release type sub-fields
        case 'collectionName':
        case 'boxName':
        case 'boxTheme':
        case 'groupName':
        case 'groupTheme':
        case 'exclusiveTo':
        case 'exclusiveTheme':
        case 'charityName':
        case 'charityCause':
        case 'conventionName':
        case 'collabWith':
        case 'collabTheme':
        case 'setName':
        case 'setType':
        case 'originalReleaseSeason':
        case 'releaseNotes':
        case 'baseType':
        case 'colorShift':
        case 'isoPriority':
        case 'destashCondition':
        case 'barcode':
        case 'lastUsedDate':
        case 'line':
        case 'swatchNumber':
          if (value.trim()) {
            mapped[field] = value.trim();
          }
          break;
      }
    });
    
    // Auto-detect polish type if not already set
    // Only run detection if we have both brand and name
    if (!mapped.polishType && mapped.brand && mapped.name) {
      const detected = detectPolishType(mapped.name, mapped.brand);
      if (detected) {
        mapped.polishType = detected;
      }
    }
    
    // Validate required fields
    if (!mapped.brand) {
      mapped._errors.push('Missing brand');
    }
    if (!mapped.name) {
      mapped._errors.push('Missing name');
    }
    
    return mapped;
  });
}

export function validateMappedData(mappedRows: MappedRow[]): {
  valid: MappedRow[];
  invalid: MappedRow[];
  duplicates: MappedRow[];
  stats: {
    total: number;
    valid: number;
    invalid: number;
    duplicates: number;
  };
} {
  const valid: MappedRow[] = [];
  const invalid: MappedRow[] = [];
  const seen = new Set<string>();
  const duplicates: MappedRow[] = [];
  
  for (const row of mappedRows) {
    // Check for validation errors
    if (row._errors.length > 0) {
      invalid.push(row);
      continue;
    }
    
    // Check for duplicates (same brand + name)
    const key = `${row.brand.toLowerCase()}|${row.name.toLowerCase()}`;
    if (seen.has(key)) {
      row._errors.push('Duplicate entry in file');
      duplicates.push(row);
      continue;
    }
    
    seen.add(key);
    valid.push(row);
  }
  
  return {
    valid,
    invalid,
    duplicates,
    stats: {
      total: mappedRows.length,
      valid: valid.length,
      invalid: invalid.length,
      duplicates: duplicates.length
    }
  };
}

export function getUnmappedRequired(mapping: ColumnMapping, itemType: ImportItemType = 'polish'): string[] {
  const mappedFields = new Set(Object.values(mapping).filter(Boolean));
  const unmapped: string[] = [];
  const fields = getFieldsForType(itemType);
  
  for (const [field, config] of Object.entries(fields)) {
    if (config.required && !mappedFields.has(field)) {
      unmapped.push(field);
    }
  }
  
  return unmapped;
}

export function generateTemplate(itemType: ImportItemType = 'polish'): string {
  switch (itemType) {
    case 'polish':
      return [
        ['Brand', 'Polish Name', 'Category', 'Color Description', 'Line', 'Swatch Number', 'Finish', 'Color Family', 'Quantity', 'Collection', 'Event', 'Event Theme', 'Release Type', 'Release Year', 'Tags', 'Notes', 'Favorite'],
        ['Sally Hansen', 'Cherry Red', 'Nail Lacquer', 'Bright cherry red creme', 'Insta-Dri', '42', 'creme', 'red', '1', '', '', '', 'Core', '2023', 'one coater, fast dry', '', 'Yes'],
        ['ILNP', 'Eclipse', 'Nail Lacquer', 'Deep blue with holo sparkle', 'Ultra Chrome', '', 'linear holo', 'blue', '1', 'Ultra Holos', '', '', 'Core', '2020', 'untried', 'Beautiful formula!', 'No'],
        ['Polished for Days', 'Rewind', 'Nail Lacquer', 'Purple thermal', '', '', 'thermal, shimmer', 'purple', '2', '', 'Polish Pickup', 'Rewind', 'Event', '2022', 'indie', '', 'No']
      ].map(row => row.join(',')).join('\n');
      
    case 'plate':
      return [
        ['Brand', 'Plate Name', 'Plate Type', 'Plate Size', 'Plate Shape', 'Image Count', 'Quantity', 'Collection', 'Tags', 'Notes', 'Storage'],
        ['MoYou', 'Bridal 01', 'Stamping Plate', 'Large', 'Rectangle', '12', '1', 'Bridal Collection', 'lace, wedding, floral', 'Great for weddings!', 'Drawer 1'],
        ['Maniology', 'BM-XL301', 'Stamping Plate', 'XL', 'Rectangle', '20', '1', '', 'geometric, abstract', '', 'Drawer 2']
      ].map(row => row.join(',')).join('\n');
      
    case 'transfer':
      return [
        ['Brand', 'Decal Name', 'Type', 'Quantity', 'Collection', 'Tags', 'Notes', 'Storage'],
        ['Born Pretty', 'Butterfly Dreams', 'Waterslide', '3', 'Spring 2023', 'butterfly, nature', 'Beautiful colors!', 'Box A'],
        ['Maniology', 'Foil Set 1', 'Foil', '1', '', 'gold, silver, metallic', '', 'Box B']
      ].map(row => row.join(',')).join('\n');
      
    case 'embellishment':
      return [
        ['Brand', 'Name', 'Type', 'Color', 'Size', 'Quantity', 'Tags', 'Notes', 'Storage'],
        ['Daily Charme', 'Rose Charms', 'Charm', 'Gold', 'Small', '10', 'roses, floral', 'Super cute!', 'Charm Box'],
        ['Born Pretty', 'Crystal Mix', 'Rhinestone', 'AB Clear', 'Mixed', '1', 'sparkle, crystals', '', 'Rhinestone Wheel']
      ].map(row => row.join(',')).join('\n');
      
    case 'tool':
      return [
        ['Brand', 'Tool Name', 'Type', 'Material', 'Quantity', 'Tags', 'Notes', 'Storage'],
        ['Twinkled T', 'Cleanup Brush', 'Cleanup', 'Synthetic', '2', 'cleanup, brush', 'Best cleanup brush!', 'Tool Holder'],
        ['Maniology', 'Dotting Tool Set', 'Dotting Tool', 'Metal', '1', 'dots, nail art', 'Various sizes', 'Tool Drawer']
      ].map(row => row.join(',')).join('\n');
      
    case 'powder':
      return [
        ['Brand', 'Powder Name', 'Type', 'Color', 'Effect', 'Quantity', 'Tags', 'Notes', 'Storage'],
        ['Daily Charme', 'Aurora Unicorn', 'Aurora', 'Pink/Blue', 'Color shift', '1', 'chrome, unicorn', 'Beautiful shift!', 'Powder Box'],
        ['Born Pretty', 'Mirror Chrome', 'Chrome', 'Silver', 'Mirror', '2', 'chrome, silver, mirror', '', 'Powder Drawer']
      ].map(row => row.join(',')).join('\n');
      
    case 'acrylic':
      return [
        ['Brand', 'Acrylic Name', 'Type', 'Color', 'Quantity', 'Tags', 'Notes', 'Storage'],
        ['Mia Secret', 'Cover Pink', 'Powder', 'Pink', '1', 'cover, pink, natural', 'Great coverage', 'Acrylic Shelf'],
        ['Young Nails', 'Synergy Liquid', 'Liquid', 'Clear', '1', 'monomer, liquid', '', 'Acrylic Cabinet']
      ].map(row => row.join(',')).join('\n');
      
    default:
      return '';
  }
}
